import React from "react";
import { EmailPreviewDialog, resolveCustomerReplyTo } from "./EmailPreviewDialog";

/**
 * Read-only preview of the Rebook Reminder email.
 *
 * Renders the exact subject + body that will be sent (with {customer_name}
 * and {business_name} interpolated), plus the headers we control so the
 * owner can verify the From / Reply-To routing before clicking send. This
 * is *display only* — opening this modal must NEVER trigger any network
 * call. The send action lives next to the trigger and is unchanged by
 * this component.
 *
 * Optional `ctaUrl` renders the same "Request Another Cleaning" button the
 * customer will see in the actual email. We only render the button when a
 * URL is provided so the preview accurately reflects what the customer
 * gets — nothing more, nothing less.
 *
 * Visual shell (header card with From / Reply-To / Subject + body slot) is
 * delegated to `EmailPreviewDialog` so this component and
 * `ReviewRequestPreviewDialog` stay byte-identical in layout. Testid
 * stability: legacy testids `rebook-preview-{from,reply-to,subject,dialog}`
 * are preserved via the `testidPrefix="rebook-preview"` prop so existing
 * tests/agents keep working.
 */
export const RebookPreviewDialog = ({
  open,
  onOpenChange,
  customerName,
  user,
  ctaUrl = null,
  triggerTestId, // legacy alias accepted but unused — kept for callsite compat
}) => {
  const businessName = (user?.business_name || "your cleaner").trim();
  const safeName = (customerName || "there").trim() || "there";
  const replyTo = resolveCustomerReplyTo(user);
  // triggerTestId is intentionally ignored — the shared shell uses
  // `testidPrefix + "-dialog"` instead, which keeps the existing
  // `rebook-preview-dialog` selector working.
  void triggerTestId;

  return (
    <EmailPreviewDialog
      open={open}
      onOpenChange={onOpenChange}
      subject="Ready for your next cleaning?"
      replyTo={replyTo}
      sendButtonLabel="Send Rebook Reminder"
      testidPrefix="rebook-preview"
    >
      <div
        className="rounded-md border border-gray-200 bg-white px-4 py-3 text-gray-800 leading-relaxed whitespace-pre-line"
        data-testid="rebook-preview-body"
      >
        {`Hi ${safeName},

We hope you've been enjoying your clean home 😊

If you'd like to schedule another cleaning, we'd love to help again.`}
        {ctaUrl ? (
          <div className="my-3" data-testid="rebook-preview-cta">
            <a
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 rounded-md bg-green-800 hover:bg-green-900 text-white text-sm font-semibold no-underline"
              data-testid="rebook-preview-cta-link"
            >
              Request Another Cleaning
            </a>
          </div>
        ) : null}
        {`You can reply to this email or request another quote anytime.

Thank you,
${businessName}`}
      </div>
    </EmailPreviewDialog>
  );
};
